/**
 * Account: client schemas and mappers.
 * Endpoints: /api/client/auth/login, /register, /me, /profile/update, /forgot-password, /reset-password,
 * /api/stats/client/dashboard.
 */
import { z } from 'zod';
import { messageMap, ns, translate } from '@/i18n';
import type { User } from '../../domain';

const s = z.string().nullish();

export const ClientSchema = z
  .object({
    clientId: s,
    _id: s,
    clientname: s,
    clientName: s,
    firstName: s,
    lastName: s,
    email: s,
    phoneNumber: s,
    createdAt: s,
    profilePicture: s,
    dateOfBirth: s,
    bio: s,
    marketingConsent: z.boolean().nullish(),
  })
  .passthrough();
export type ClientRecord = z.infer<typeof ClientSchema>;

export const AuthResponseSchema = z.object({ token: z.string(), client: ClientSchema }).passthrough();
export const ProfileUpdateSchema = z.object({ client: ClientSchema }).passthrough();
export const ForgotPasswordSchema = z.object({ message: s, clientId: s }).passthrough();

/**
 * Profile photo URL (IMG-4): uploads are served at `/uploads/profile-pictures/<file>` (not under `/api`);
 * Google photos are absolute URLs.
 */
export function avatarUrl(baseUrl: string, stored: string | null | undefined): string | undefined {
  const v = stored?.trim();
  if (!v) return undefined;
  if (/^https?:\/\//i.test(v)) return v;
  return `${baseUrl.replace(/\/$/, '')}/uploads/profile-pictures/${encodeURIComponent(v.replace(/^.*\//, ''))}`;
}

/** `baseUrl` (API origin) is needed for the photo URL: without it the user carries no `avatar`. */
export function toUser(c: ClientRecord, baseUrl?: string): User {
  const id = String(c.clientId ?? c._id ?? '');
  const name =
    (c.clientname ?? c.clientName ?? '').trim() ||
    [c.firstName, c.lastName].filter(Boolean).join(' ').trim() ||
    (c.email ?? '').split('@')[0] ||
    translate(ns.labels, 'customer');
  return {
    id,
    name,
    email: c.email ?? '',
    phone: c.phoneNumber ?? '',
    memberSince: (c.createdAt ?? new Date().toISOString()).slice(0, 10),
    // Consent modal (P11): asked while not `true` (null = never answered, as after a Google sign-up).
    ...(c.marketingConsent !== undefined ? { marketingConsent: c.marketingConsent } : {}),
    ...(baseUrl !== undefined
      ? {
          avatar: avatarUrl(baseUrl, c.profilePicture),
          dateOfBirth: c.dateOfBirth ? c.dateOfBirth.slice(0, 10) : undefined,
          bio: c.bio?.trim() || undefined,
        }
      : {}),
  };
}

/** Auth error codes of the backend → our message, in the current language. */
export const AUTH_MESSAGES = messageMap(ns.errors, {
  INVALID_CREDENTIALS: 'auth.INVALID_CREDENTIALS',
  ACCOUNT_NOT_FOUND: 'auth.ACCOUNT_NOT_FOUND',
  GOOGLE_AUTH_ACCOUNT: 'auth.GOOGLE_AUTH_ACCOUNT',
  // Google sign-in (P7, `POST /api/client/auth/google`)
  INVALID_GOOGLE_TOKEN: 'auth.INVALID_GOOGLE_TOKEN',
  ACCOUNT_CONFLICT: 'auth.ACCOUNT_CONFLICT',
  GOOGLE_SIGNIN_ERROR: 'auth.GOOGLE_SIGNIN_ERROR',
  EMAIL_EXISTS: 'auth.EMAIL_EXISTS',
  DB_INDEX_CONFLICT: 'auth.DB_INDEX_CONFLICT',
  INVALID_PHONE_NUMBER: 'auth.INVALID_PHONE_NUMBER',
  INVALID_CLIENT_NAME: 'auth.INVALID_CLIENT_NAME',
  INVALID_DATE_OF_BIRTH: 'auth.INVALID_DATE_OF_BIRTH',
  INVALID_BIO: 'auth.INVALID_BIO',
  IMAGE_TOO_LARGE: 'auth.IMAGE_TOO_LARGE',
  UNSUPPORTED_IMAGE_TYPE: 'auth.UNSUPPORTED_IMAGE_TYPE',
  UPLOAD_FAILED: 'auth.UPLOAD_FAILED',
});

export const authMessage = (code: string | undefined): string | undefined =>
  code && code in AUTH_MESSAGES ? AUTH_MESSAGES[code as keyof typeof AUTH_MESSAGES] : undefined;
